import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Clock, ChevronRight } from "lucide-react";
import { Header } from "../sections/Header";
import { Footer } from "../sections/Footer";
import { StickyCTA } from "../sections/StickyCTA";
import {
  Seo,
  buildBlogSchema,
  buildBreadcrumbSchema,
  ORGANIZATION_SCHEMA,
} from "../components/Seo";
import { INSIGHTS } from "../data/insights";

const Insights = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const title = "Insights — Forensic Accounting & Financial Litigation";
  const description =
    "Long-form analysis on forensic accounting, business litigation, fraudulent transfers, lost-profits damages, and expert witness work by Richard L. Bufkin, attorney, CPA, and AICPA CFF.";

  return (
    <>
      <Seo
        title={title}
        description={description}
        path="/insights"
        type="website"
        keywords="forensic accounting blog, business litigation insights, expert witness analysis, AICPA CFF, Texas attorney blog"
        schemas={[
          ORGANIZATION_SCHEMA,
          buildBlogSchema(INSIGHTS),
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Insights", path: "/insights" },
          ]),
        ]}
      />
      <main data-testid="insights-page" className="bg-ivory min-h-screen">
        <Header />

        {/* Hero */}
        <section
          data-testid="insights-hero"
          className="relative pt-36 lg:pt-44 pb-16 lg:pb-20 bg-ivory overflow-hidden"
        >
          <div className="absolute inset-0 pointer-events-none opacity-[0.04]">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 1px 1px, #102845 1px, transparent 0)",
                backgroundSize: "32px 32px",
              }}
            />
          </div>
          <div className="absolute top-20 -right-40 w-[500px] h-[500px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />

          <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
            <nav
              data-testid="insights-breadcrumb"
              className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-navy/60 mb-10 fade-up"
            >
              <Link to="/" className="hover:text-navy transition-colors">
                Home
              </Link>
              <ChevronRight className="w-3 h-3" strokeWidth={2} />
              <span className="text-navy font-medium">Insights</span>
            </nav>

            <div className="flex items-center gap-3 mb-6 fade-up-delay-1">
              <div className="gold-line" />
              <span className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium">
                From the Practice
              </span>
            </div>
            <h1
              data-testid="insights-headline"
              className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-navy leading-[1.02] tracking-tight fade-up-delay-2"
            >
              <span className="italic text-gold">Insights.</span>
            </h1>
            <p
              data-testid="insights-subhead"
              className="mt-8 text-lg lg:text-xl text-navy/75 max-w-3xl leading-relaxed fade-up-delay-3"
            >
              Long-form analysis on the matters this firm handles every day —
              forensic accounting methodology, fraudulent-transfer fact
              patterns, expert-witness strategy, and the financial issues that
              decide cases.
            </p>
          </div>
        </section>

        {/* Article grid */}
        <section className="py-16 lg:py-24 bg-cream">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid gap-px bg-[#C0974E]/15 border border-[#C0974E]/15 rounded-sm overflow-hidden">
              {INSIGHTS.map((post, idx) => (
                <Link
                  key={post.slug}
                  to={`/insights/${post.slug}`}
                  data-testid={`insight-card-${idx}`}
                  className="group bg-white p-8 lg:p-12 hover:bg-ivory transition-all duration-500"
                >
                  <div className="grid lg:grid-cols-12 gap-6 lg:gap-12 items-start">
                    <div className="lg:col-span-2">
                      <span className="font-serif text-5xl lg:text-6xl text-gold/30">
                        0{idx + 1}
                      </span>
                    </div>
                    <div className="lg:col-span-8">
                      <div className="flex items-center gap-4 mb-4">
                        <span className="text-[11px] uppercase tracking-[0.22em] text-gold font-medium">
                          {post.category}
                        </span>
                        <span className="flex items-center gap-1.5 text-xs text-navy/55">
                          <Clock className="w-3 h-3" strokeWidth={1.5} />
                          {post.readTime}
                        </span>
                        <span className="text-xs text-navy/55">
                          {new Date(post.datePublished).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </span>
                      </div>
                      <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-navy leading-[1.1] tracking-tight group-hover:text-gold transition-colors">
                        {post.title}
                      </h2>
                      <p className="mt-4 text-base lg:text-lg text-navy/70 leading-relaxed max-w-2xl">
                        {post.excerpt}
                      </p>
                    </div>
                    <div className="lg:col-span-2 lg:flex lg:justify-end">
                      <div className="w-12 h-12 rounded-sm bg-navy/5 flex items-center justify-center group-hover:bg-navy group-hover:text-gold transition-all">
                        <ArrowUpRight
                          className="w-5 h-5 text-navy group-hover:text-gold group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                          strokeWidth={1.5}
                        />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 lg:py-24 bg-ivory">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <div className="inline-flex items-center gap-3 mb-6 justify-center">
              <div className="gold-line" />
              <span className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium">
                Have a Matter?
              </span>
              <div className="gold-line" />
            </div>
            <h2 className="font-serif text-3xl lg:text-4xl text-navy leading-tight tracking-tight">
              Insights help — a conversation helps more.
            </h2>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <a
                href="/#contact"
                data-testid="insights-cta"
                className="btn-primary inline-flex items-center gap-3 px-7 py-4 text-xs tracking-[0.25em] uppercase font-medium group"
              >
                Schedule a Consultation
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </section>

        <Footer />
        <StickyCTA />
      </main>
    </>
  );
};

export default Insights;
